import type { Me } from "../api.js";

/**
 * Which scope a staff account can ask a report for.
 *
 * Two independent facts decide this, and conflating them is what broke the
 * reports page for the office.
 *
 * **`scope=organisation`** is a permission: `reports.organisation.read`, which
 * an owner grants. **`scope=mine`** is not a permission at all — it is scoped
 * by the account's Amelia provider id, so it is available to whoever has one
 * and impossible for whoever does not, and no grant changes that. Somebody in
 * the office has a tutor profile, because that is how a staff account is held
 * here, and no provider, because they do not teach.
 *
 * The page used to read only the first: anybody without
 * `reports.organisation.read` was defaulted to `mine` and had the control
 * hidden, so an office account was pinned to the one scope it could never be
 * answered for and every panel came back with FastAPI's "The staff account is
 * not linked to an Amelia provider". Both facts are needed to get this right,
 * which is why `/me` now carries the second.
 *
 * Decided here rather than in each page so that the reports page and the
 * exception list on the waiting page cannot drift apart — they had the same
 * bug, because they each worked it out for themselves.
 */
export interface ScopeChoice {
  /** Where to start. Never a scope the account cannot be answered for. */
  initial: "mine" | "organisation";
  /** Whether to offer the control at all: there has to be a real choice. */
  choosable: boolean;
  /** False when neither scope can be asked for, so nothing is worth drawing. */
  possible: boolean;
}

export function scopeChoice(me: Me | null): ScopeChoice {
  const own = me?.linkedToProvider ?? false;
  const school = (me?.capabilities ?? []).includes("reports.organisation.read");

  return {
    // Whole-school where it is held, because somebody who can see everything
    // came for everything. Otherwise their own — unless they have no provider,
    // in which case "their own" does not exist and the only scope that could
    // ever answer is the one they are about to be told they need.
    initial: school || !own ? "organisation" : "mine",
    choosable: own && school,
    possible: own || school,
  };
}

/**
 * What to tell somebody who can be asked nothing.
 *
 * Ours rather than FastAPI's. The upstream message is accurate and useless to
 * the person reading it — it names an Amelia provider, which is not a thing
 * anybody in the office has heard of, and does not say what would fix it. This
 * says what they need and who can give it to them.
 */
export const NEEDS_ORGANISATION_SCOPE =
  "This account is not linked to a teacher in the booking system, so there are no lessons of its own to report on. Seeing the school's figures needs the whole-school reports permission, which an owner can grant on the staff page.";
